import React, { useState, useEffect, useRef, lazy, Suspense } from 'react';
import JSZip from 'jszip';
import FileSaver from 'file-saver';
import { INITIAL_STATE, AppState, CloudProvider, Tool, Override } from './types';
import { REGIONS, POPULAR_MODULES } from './constants';
import { fetchGithubVariables } from './services/githubService';
import { parseVariablesLocally } from './services/localParserService';
import { generateFilesLocally } from './services/localGeneratorService';
import { validateConfiguration } from './services/geminiService';
import { formatTerraformValue, convertJsValueToHcl } from './utils/hclFormatter';
import StepCard from './components/StepCard';
import CodeBlock from './components/CodeBlock';
import { Icons } from './components/Icons';
import Logo from './components/Logo';
import Tooltip from './components/Tooltip';
import Modal from './components/Modal';
import HelpPage from './components/HelpPage';
import AdUnit from './components/AdUnit';
import FeedbackModal from './components/FeedbackModal';
import ValidationModal from './components/ValidationModal';
import SeoContent from './components/SeoContent';
import { PrivacyPage, TermsPage } from './components/LegalPages';
import TopNav from './components/TopNav';

// Lazy-loaded content pages for code splitting
const TerraformOverrideGuide = lazy(() => import('./components/pages/TerraformOverrideGuide'));
const TerraformExamples = lazy(() => import('./components/pages/TerraformExamples'));
const TerraformPrecedence = lazy(() => import('./components/pages/TerraformPrecedence'));
const TerragruntOverrides = lazy(() => import('./components/pages/TerragruntOverrides'));
const AboutUs = lazy(() => import('./components/pages/AboutUs'));
const ContactUs = lazy(() => import('./components/pages/ContactUs'));
const TerraformBestPractices = lazy(() => import('./components/pages/TerraformBestPractices'));
const TerraformTroubleshooting = lazy(() => import('./components/pages/TerraformTroubleshooting'));

// Define View State Type
type ViewState =
  | 'home'
  | 'guide'
  | 'privacy'
  | 'terms'
  | 'about'
  | 'contact'
  | 'guide-overrides'
  | 'guide-examples'
  | 'guide-precedence'
  | 'guide-terragrunt'
  | 'guide-best-practices'
  | 'guide-troubleshooting'
  | 'not-found';

// Map internal views to SEO-friendly URL paths
const getPathFromView = (view: ViewState): string => {
  switch (view) {
    case 'guide-overrides':
      return '/terraform-override-files-guide';
    case 'guide-examples':
      return '/terraform-override-examples';
    case 'guide-precedence':
      return '/terraform-variable-precedence';
    case 'guide-terragrunt':
      return '/terragrunt-override-blocks';
    case 'guide-best-practices':
      return '/terraform-override-best-practices';
    case 'guide-troubleshooting':
      return '/terraform-override-troubleshooting';
    case 'privacy':
      return '/privacy';
    case 'terms':
      return '/terms';
    case 'about':
      return '/about';
    case 'contact':
      return '/contact';
    case 'guide':
      return '/guide';
    case 'home':
      return '/';
    case 'not-found':
      return '/404';
    default:
      return '/';
  }
};

const getViewFromPath = (path: string): ViewState => {
  // Normalize path
  const normalizedPath = path.toLowerCase();

  if (normalizedPath === '/' || normalizedPath === '') return 'home';

  // Clean trailing slashes for standard pages
  const cleanPath =
    normalizedPath.endsWith('/') && normalizedPath.length > 1
      ? normalizedPath.slice(0, -1)
      : normalizedPath;

  if (cleanPath === '/terraform-override-files-guide') return 'guide-overrides';
  if (cleanPath === '/terraform-override-examples') return 'guide-examples';
  if (cleanPath === '/terraform-variable-precedence') return 'guide-precedence';
  if (cleanPath === '/terragrunt-override-blocks') return 'guide-terragrunt';
  if (cleanPath === '/terraform-override-best-practices') return 'guide-best-practices';
  if (cleanPath === '/terraform-override-troubleshooting') return 'guide-troubleshooting';
  if (cleanPath === '/privacy') return 'privacy';
  if (cleanPath === '/terms') return 'terms';
  if (cleanPath === '/about') return 'about';
  if (cleanPath === '/contact') return 'contact';
  if (cleanPath === '/guide') return 'guide';

  return 'not-found';
};

const App: React.FC = () => {
  const [state, setState] = useState<AppState>(INITIAL_STATE);
  const [newVarValue, setNewVarValue] = useState('');
  const [selectedVarName, setSelectedVarName] = useState('');
  const [activeTab, setActiveTab] = useState<'provider' | 'main' | 'overrides'>('provider');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [headerCopied, setHeaderCopied] = useState(false);
  const [validationOpen, setValidationOpen] = useState(false);

  // --- ROUTING LOGIC START ---
  // Initialize view based on current URL (Support deep linking/indexing)
  const [currentView, setCurrentView] = useState<ViewState>(() => {
    return getViewFromPath(window.location.pathname);
  });

  // Custom Navigation function (pushState + state update)
  const navigateTo = (view: ViewState) => {
    const path = getPathFromView(view);
    window.history.pushState({}, '', path);
    setCurrentView(view);
    window.scrollTo(0, 0);
  };

  // Handle browser back/forward buttons
  useEffect(() => {
    const handlePopState = () => {
      setCurrentView(getViewFromPath(window.location.pathname));
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);
  // --- ROUTING LOGIC END ---

  // Modal States
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [modalConfig, setModalConfig] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: 'warning' | 'danger' | 'info';
    confirmText?: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    type: 'warning',
    confirmText: 'Confirm',
    onConfirm: () => {},
  });

  // Derived state
  const selectedVarDef = state.variables.find((v) => v.name === selectedVarName);
  const isModuleLocked = state.variables.length > 0;
  const isEditing = state.stagedOverrides.some((o) => o.name === selectedVarName);

  // Dark Mode State
  const [darkMode, setDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      return (
        localStorage.getItem('theme') === 'dark' ||
        (!localStorage.getItem('theme') &&
          window.matchMedia('(prefers-color-scheme: dark)').matches)
      );
    }
    return false;
  });

  // Apply Dark Mode
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [darkMode]);

  const toggleDarkMode = () => setDarkMode(!darkMode);

  // --- SEO MANAGEMENT START ---
  // Updates Title, Meta Tags, Canonical Link, and JSON-LD based on currentView
  useEffect(() => {
    let title = 'TF Override Generator | Create Terraform, Terragrunt & OpenTofu Overrides';
    let description =
      'The free AI-powered tool for DevOps engineers to instantly generate overrides.tf, terragrunt.hcl inputs, and provider configurations.';
    let jsonLd = null;
    let canonicalPath = getPathFromView(currentView);

    switch (currentView) {
      case 'guide':
        title = 'How to Use | TF Override Generator Guide';
        description =
          'Step-by-step guide and documentation for using the TF Override Generator tool. Learn how to generate Terraform overrides quickly.';
        break;
      case 'privacy':
        title = 'Privacy Policy | TF Override Generator';
        description =
          'Privacy policy for TF Override Generator. Learn how we collect, use, and protect your data when using our Terraform override generation tool.';
        break;
      case 'terms':
        title = 'Terms of Service | TF Override Generator';
        description =
          'Terms of service for TF Override Generator. Understand your rights and responsibilities when using our tool to generate Terraform configurations.';
        break;
      case 'about':
        title = 'About Us | TF Override Generator - Built by DevOps Engineers';
        description =
          'Learn about TF Override Generator, created by experienced DevOps engineers with 10+ years in Terraform, AWS, Azure, GCP, and infrastructure automation.';
        break;
      case 'contact':
        title = 'Contact Us | TF Override Generator Support';
        description =
          'Get in touch with TF Override Generator. Contact us for support, feedback, bug reports, or feature requests. Email: help@tfoverrides.dev';
        break;
      case 'guide-overrides':
        title = 'Terraform Override Files Guide | How _override.tf Works';
        description =
          'Master Terraform override files. Learn how merging works for resources, lists, and maps, and when to use overrides vs variables.';
        jsonLd = {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: [
            {
              '@type': 'Question',
              name: 'What is an override file in Terraform?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Files ending in _override.tf are loaded last by Terraform. Their content is merged with existing configuration, allowing you to modify resources without editing the original files.',
              },
            },
            {
              '@type': 'Question',
              name: 'Do overrides replace resources?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Not entirely. Terraform merges arguments within resources. However, list attributes are fully replaced, while map attributes are merged.',
              },
            },
          ],
        };
        break;
      case 'guide-examples':
        title = 'Terraform Override Examples | AWS, Azure, & GCP Patterns';
        description =
          'Copy-pasteable Terraform override examples for AWS EC2, Azure AKS, and Google Cloud Storage. Learn to patch modules without forking code.';
        break;
      case 'guide-precedence':
        title = 'Terraform Variable Precedence Explained | Definitive Guide';
        description =
          'Understand the exact order of variable precedence in Terraform: Environment variables vs tfvars vs CLI flags.';
        break;
      case 'guide-terragrunt':
        title = 'Terragrunt Overrides & Inputs | Generator Tool';
        description =
          'How to use inputs and generate blocks in Terragrunt to override Terraform variables dynamically.';
        break;
      case 'not-found':
        title = '404 Not Found | TF Override Generator';
        description = 'The page you are looking for does not exist.';
        canonicalPath = '/404';
        break;
      case 'home':
      default:
        if (isAnalyzing) {
          title = 'Analyzing Module... | TF Override Generator';
        } else if (state.moduleUrl) {
          // Dynamic title based on user input
          let repoName = 'Custom Module';
          try {
            const parts = state.moduleUrl.split('/');
            if (parts.length >= 2) {
              repoName = parts[parts.length - 1] || parts[parts.length - 2];
            }
          } catch {
            /* ignore URL parse errors */
          }
          const tool = state.tool.charAt(0).toUpperCase() + state.tool.slice(1);
          const provider = state.provider.toUpperCase();
          title = `${tool} ${provider} ${repoName} Generator | TF Override Generator`;
        }
    }

    // Update Title
    document.title = title;

    // Update Meta Description
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', description);

    // Update Canonical URL
    const canonicalLink = document.querySelector('link[rel="canonical"]');
    if (canonicalLink) {
      canonicalLink.setAttribute('href', `https://tfoverrides.dev${canonicalPath}`);
    }

    // Inject JSON-LD
    const existingScript = document.getElementById('json-ld-data');
    if (existingScript) existingScript.remove();

    if (jsonLd) {
      const script = document.createElement('script');
      script.id = 'json-ld-data';
      script.type = 'application/ld+json';
      script.text = JSON.stringify(jsonLd);
      document.head.appendChild(script);
    }

    // Report 404 status via meta tag if needed (for googlebot to see on client render)
    // Note: True 404s require server side headers, but this helps.
    const prerenderStatus = document.querySelector('meta[name="prerender-status-code"]');
    if (currentView === 'not-found') {
      if (!prerenderStatus) {
        const meta = document.createElement('meta');
        meta.name = 'prerender-status-code';
        meta.content = '404';
        document.head.appendChild(meta);
      }
    } else {
      if (prerenderStatus) prerenderStatus.remove();
    }
  }, [currentView, isAnalyzing, state.moduleUrl, state.tool, state.provider]);
  // --- SEO MANAGEMENT END ---

  // --- GA4 VIRTUAL PAGE VIEW TRACKING ---
  // Skips first render because index.html gtag('config') handles the initial page_view
  const gaFirstRender = useRef(true);
  useEffect(() => {
    if (gaFirstRender.current) {
      gaFirstRender.current = false;
      return;
    }
    const path = getPathFromView(currentView);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).gtag?.('event', 'page_view', {
      page_path: path,
      page_title: document.title,
      page_location: `https://tfoverrides.dev${path}`,
    });
  }, [currentView]);
  // --- GA4 VIRTUAL PAGE VIEW TRACKING END ---

  // 404 Graceful redirect with countdown
  const [countdown, setCountdown] = React.useState(5);

  useEffect(() => {
    if (currentView === 'not-found') {
      // Update countdown every second
      const countdownInterval = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(countdownInterval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      // Redirect after 5 seconds
      const redirectTimer = setTimeout(() => {
        navigateTo('home');
        // Show notification after redirect
        setTimeout(() => {
          setModalConfig({
            isOpen: true,
            title: 'Page Not Found',
            message:
              'The page you were looking for does not exist. You have been redirected to the home page.',
            type: 'info',
            confirmText: 'Got it',
            onConfirm: closeModal,
          });
        }, 100);
      }, 5000);

      return () => {
        clearInterval(countdownInterval);
        clearTimeout(redirectTimer);
      };
    } else {
      setCountdown(5); // Reset countdown when not on 404 page
    }
  }, [currentView]);

  if (currentView === 'not-found') {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex flex-col items-center justify-center text-center p-4">
        <div className="max-w-md mx-auto">
          <Icons.AlertCircle className="w-20 h-20 text-amber-500 dark:text-amber-400 mx-auto mb-6" />
          <h1 className="text-6xl font-bold text-slate-900 dark:text-white mb-4">404</h1>
          <p className="text-xl text-slate-600 dark:text-slate-400 mb-2">Page Not Found</p>
          <p className="text-sm text-slate-500 dark:text-slate-500 mb-8">
            The page you're looking for doesn't exist or has been moved.
            <br />
            Redirecting to home in{' '}
            <span className="font-semibold text-indigo-600 dark:text-indigo-400">
              {countdown}
            </span>{' '}
            second{countdown !== 1 ? 's' : ''}...
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => navigateTo('home')}
              className="min-h-[44px] px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium"
            >
              Go Home Now
            </button>
            <button
              onClick={() => window.history.back()}
              className="min-h-[44px] px-6 py-3 border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors font-medium"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Routing — lazy-loaded pages wrapped in Suspense
  const PageLoader = () => (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950">
      <div className="animate-pulse text-slate-400">Loading...</div>
    </div>
  );

  if (currentView === 'guide') {
    return <HelpPage onBack={() => navigateTo('home')} />;
  }
  if (currentView === 'privacy') {
    return <PrivacyPage onBack={() => navigateTo('home')} />;
  }
  if (currentView === 'terms') {
    return <TermsPage onBack={() => navigateTo('home')} />;
  }
  if (currentView === 'about') {
    return (
      <Suspense fallback={<PageLoader />}>
        <AboutUs onBack={() => navigateTo('home')} />
      </Suspense>
    );
  }
  if (currentView === 'contact') {
    return (
      <Suspense fallback={<PageLoader />}>
        <ContactUs onBack={() => navigateTo('home')} />
      </Suspense>
    );
  }
  if (currentView === 'guide-overrides') {
    return (
      <Suspense fallback={<PageLoader />}>
        <TerraformOverrideGuide onBack={() => navigateTo('home')} />
      </Suspense>
    );
  }
  if (currentView === 'guide-examples') {
    return (
      <Suspense fallback={<PageLoader />}>
        <TerraformExamples onBack={() => navigateTo('home')} />
      </Suspense>
    );
  }
  if (currentView === 'guide-precedence') {
    return (
      <Suspense fallback={<PageLoader />}>
        <TerraformPrecedence onBack={() => navigateTo('home')} />
      </Suspense>
    );
  }
  if (currentView === 'guide-terragrunt') {
    return (
      <Suspense fallback={<PageLoader />}>
        <TerragruntOverrides onBack={() => navigateTo('home')} />
      </Suspense>
    );
  }
  if (currentView === 'guide-best-practices') {
    return (
      <Suspense fallback={<PageLoader />}>
        <TerraformBestPractices onBack={() => navigateTo('home')} />
      </Suspense>
    );
  }
  if (currentView === 'guide-troubleshooting') {
    return (
      <Suspense fallback={<PageLoader />}>
        <TerraformTroubleshooting onBack={() => navigateTo('home')} />
      </Suspense>
    );
  }

  // Modal Handler Helper
  const openModal = (
    title: string,
    message: string,
    onConfirm: () => void,
    type: 'warning' | 'danger' | 'info' = 'warning',
    confirmText: string = 'Confirm'
  ) => {
    setModalConfig({
      isOpen: true,
      title,
      message,
      type,
      confirmText,
      onConfirm,
    });
  };

  const closeModal = () => {
    setModalConfig((prev) => ({ ...prev, isOpen: false }));
  };

  // Handlers
  const handleResetRequest = () => {
    openModal(
      'Reset Project',
      'Are you sure you want to reset all progress? This will clear your current configuration and overrides.',
      () => {
        setState(INITIAL_STATE);
        setSelectedVarName('');
        setNewVarValue('');
      },
      'danger',
      'Reset Project'
    );
  };

  const handleChangeModuleRequest = () => {
    openModal(
      'Change Module',
      'Changing the module source will clear your currently fetched variables and staged overrides. Do you want to proceed?',
      () => {
        // Clear variables and overrides, but keep the current URL so user can edit it
        setState((prev) => ({
          ...prev,
          variables: [],
          stagedOverrides: [],
          generatedFiles: null,
          error: null,
        }));
        setSelectedVarName('');
        setNewVarValue('');
      },
      'warning',
      'Proceed'
    );
  };

  const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setState((prev) => ({ ...prev, moduleUrl: e.target.value }));
  };

  // Region Change with Confirmation
  const handleRegionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newRegion = e.target.value;

    if (state.stagedOverrides.length > 0 || state.generatedFiles) {
      openModal(
        'Confirm Region Change',
        'Changing the region after staging variables may require regenerating your configuration files. Continue?',
        () => {
          setState((prev) => ({ ...prev, region: newRegion, generatedFiles: null }));
        },
        'warning',
        'Update Region'
      );
    } else {
      setState((prev) => ({ ...prev, region: newRegion }));
    }
  };

  // Helper to validate URL
  const isValidUrl = (urlString: string) => {
    try {
      return Boolean(new URL(urlString));
    } catch {
      return false;
    }
  };

  // Core Analysis Logic
  const performAnalysis = async () => {
    setIsAnalyzing(true);
    setState((prev) => ({
      ...prev,
      loadingMessage: 'Fetching and analyzing module structure...',
      error: null,
    }));

    try {
      // 1. Fetch Raw Content
      const rawHcl = await fetchGithubVariables(state.moduleUrl);

      // Check for empty content
      if (!rawHcl || rawHcl.trim().length === 0) {
        throw new Error("The 'variables.tf' file exists but appears to be empty.");
      }

      // 2. Parse locally — no AI, no data sent anywhere
      const variables = parseVariablesLocally(rawHcl);

      if (variables.length === 0) {
        throw new Error(
          "Could not parse any variables from this module's variables.tf. The file may use an unsupported format."
        );
      }

      setState((prev) => ({
        ...prev,
        variables,
        loadingMessage: '',
      }));

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      setState((prev) => ({
        ...prev,
        error: err.message || 'Failed to analyze module',
        variables: [],
      }));
    } finally {
      setIsAnalyzing(false);
    }
  };

  const analyzeModule = async () => {
    if (!state.moduleUrl) return;

    if (!isValidUrl(state.moduleUrl) || !state.moduleUrl.includes('github.com')) {
      setState((prev) => ({ ...prev, error: 'Please enter a valid GitHub repository URL.' }));
      return;
    }

    // Provider Compatibility Validation
    const providerKeywords: Record<string, string[]> = {
      [CloudProvider.AWS]: ['aws', 'amazon'],
      [CloudProvider.AZURE]: ['azure', 'azurerm'],
      [CloudProvider.GCP]: ['gcp', 'google'],
      [CloudProvider.OCI]: ['oci', 'oracle'],
    };

    const keywords = providerKeywords[state.provider] || [];
    const urlLower = state.moduleUrl.toLowerCase();

    const isMatch = keywords.some((k) => urlLower.includes(k));

    // Also check if it matches a DIFFERENT provider, which is a stronger signal of error
    const detectedOtherProvider = Object.keys(providerKeywords).find(
      (p) => p !== state.provider && providerKeywords[p].some((k) => urlLower.includes(k))
    );

    if (detectedOtherProvider) {
      openModal(
        'Provider Mismatch Detected',
        `This module URL appears to be for ${detectedOtherProvider.toUpperCase()}, but you have selected ${state.provider.toUpperCase()}. Parsing variables may fail or produce incorrect results. Do you want to proceed?`,
        () => performAnalysis(),
        'warning',
        'Analyze Anyway'
      );
      return;
    }

    if (!isMatch && keywords.length > 0) {
      openModal(
        'Potential Provider Mismatch',
        `The module URL does not contain standard keywords for ${state.provider.toUpperCase()}. Ensure this is the correct module for your selected provider.`,
        () => performAnalysis(),
        'info',
        'Proceed'
      );
      return;
    }

    await performAnalysis();
  };

  const getValuePlaceholder = (type?: string) => {
    if (!type) return 'Enter value';
    const t = type.toLowerCase();
    if (t === 'string') return 'Enter text (quotes added automatically)';
    if (t === 'number') return 'Enter number (e.g., 5, 10.5)';
    if (t === 'bool' || t === 'boolean') return 'true or false';
    if (t.includes('list')) return 'Comma separated values (e.g. val1, val2) or JSON array';
    if (t.includes('map') || t.includes('object'))
      return 'HCL Map or Object (e.g. { key = "value" })';
    return `Enter value for ${type}`;
  };

  const addOverride = () => {
    if (!selectedVarName || !newVarValue) return;

    // Intelligent Formatting using utility
    let formattedValue = newVarValue;
    if (selectedVarDef) {
      formattedValue = formatTerraformValue(newVarValue, selectedVarDef.type);
    }

    setState((prev) => {
      // Check if variable is already staged
      const existingIndex = prev.stagedOverrides.findIndex((o) => o.name === selectedVarName);

      if (existingIndex !== -1) {
        // Update existing override
        const updatedOverrides = [...prev.stagedOverrides];
        updatedOverrides[existingIndex] = {
          ...updatedOverrides[existingIndex],
          value: formattedValue,
        };
        return {
          ...prev,
          stagedOverrides: updatedOverrides,
        };
      } else {
        // Add new override
        const newOverride: Override = {
          id: Math.random().toString(36).substr(2, 9),
          name: selectedVarName,
          value: formattedValue,
        };
        return {
          ...prev,
          stagedOverrides: [...prev.stagedOverrides, newOverride],
        };
      }
    });

    // Reset inputs
    setNewVarValue('');
    setSelectedVarName('');
  };

  const removeOverride = (id: string) => {
    setState((prev) => ({
      ...prev,
      stagedOverrides: prev.stagedOverrides.filter((o) => o.id !== id),
    }));
  };

  const generateFiles = () => {
    setState((prev) => ({ ...prev, error: null, generatedFiles: null, validationResult: null }));

    try {
      // Generate 100% locally — no AI, no network, instant
      const generatedFiles = generateFilesLocally(state);
      setState((prev) => ({ ...prev, generatedFiles }));

      const outputEl = document.getElementById('output-section');
      if (outputEl) {
        outputEl.scrollIntoView({ behavior: 'smooth' });
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      setState((prev) => ({ ...prev, error: err.message || 'Generation failed' }));
    }
  };

  const handleValidate = async () => {
    if (!state.generatedFiles) return;
    setValidationOpen(true);

    // If we already have a result for the current files, don't re-run
    if (state.validationResult) return;

    setState((prev) => ({ ...prev, isValidating: true }));
    try {
      const result = await validateConfiguration(state.generatedFiles, state.tool);
      setState((prev) => ({ ...prev, validationResult: result }));
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      // Validation failed (API error)
      setState((prev) => ({
        ...prev,
        validationResult: {
          isValid: false,
          issues: ['Validation service error'],
          summary: err.message || 'Could not validate configuration',
        },
      }));
    } finally {
      setState((prev) => ({ ...prev, isValidating: false }));
    }
  };

  // Helper for dynamic file names based on tool selection
  const getFileName = (type: 'provider' | 'main' | 'overrides') => {
    if (state.tool === Tool.TERRAGRUNT) {
      if (type === 'provider') return 'providers.tf';
      if (type === 'main') return 'terragrunt.hcl';
      if (type === 'overrides') return 'overrides.tf';
      return 'file';
    }
    // Terraform and OpenTofu
    if (type === 'provider') return 'provider.tf';
    if (type === 'main') return 'main.tf';
    if (type === 'overrides') return 'overrides.tf';
    return 'file';
  };

  const downloadZip = async () => {
    if (!state.generatedFiles) return;

    const zip = new JSZip();
    zip.file(getFileName('provider'), state.generatedFiles.provider);
    zip.file(getFileName('main'), state.generatedFiles.main);
    zip.file(getFileName('overrides'), state.generatedFiles.overrides);

    const content = await zip.generateAsync({ type: 'blob' });

    // file-saver often has issues with named exports in some ESM environments.
    // We use the default export and fallback to property if needed.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const saveAs = (FileSaver as any).saveAs || FileSaver;
    const zipName =
      state.tool === Tool.TERRAGRUNT ? 'terragrunt-overrides.zip' : 'terraform-overrides.zip';
    saveAs(content, zipName);
  };

  const handleShareEmail = () => {
    if (!state.generatedFiles) return;

    const subject = encodeURIComponent(
      'Terraform Configuration - Generated by TF Override Generator'
    );
    const body = encodeURIComponent(
      `
Here are your generated configuration files:

--- ${getFileName('provider')} ---
${state.generatedFiles.provider}

--- ${getFileName('main')} ---
${state.generatedFiles.main}

--- ${getFileName('overrides')} ---
${state.generatedFiles.overrides}
    `.trim()
    );

    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const handleHeaderCopy = () => {
    if (!state.generatedFiles) return;
    const content = state.generatedFiles[activeTab];
    if (content) {
      navigator.clipboard.writeText(content);
      setHeaderCopied(true);
      setTimeout(() => setHeaderCopied(false), 2000);
    }
  };

  // Helper to check if generation is allowed
  const canGenerate = state.moduleUrl && state.stagedOverrides.length > 0;

  // Common Input Classes - Mobile friendly with larger touch targets
  const inputClass =
    'w-full rounded-md border-slate-300 dark:border-slate-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-base py-2.5 px-3 border bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 transition-colors disabled:opacity-60 disabled:cursor-not-allowed min-h-[44px]';
  const labelClass = 'block text-sm font-medium text-slate-700 dark:text-slate-300';

  const LabelWithTooltip = ({ label, tooltip }: { label: string; tooltip: string }) => (
    <div className="flex items-center mb-1.5">
      <label className={labelClass}>{label}</label>
      <Tooltip content={tooltip}>
        <Icons.Help className="h-4 w-4 text-slate-400 hover:text-indigo-500 dark:hover:text-indigo-400 transition-colors cursor-help" />
      </Tooltip>
    </div>
  );

  // Determine currently selected popular module to sync dropdown
  const popularModules = POPULAR_MODULES[state.provider] || [];
  const selectedPopularModule =
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    popularModules.find((m: any) => m.url === state.moduleUrl)?.url || '';

  // Helper Component for Navigation Links
  const NavLink = ({ view, children }: { view: ViewState; children?: React.ReactNode }) => {
    return (
      <a
        href={getPathFromView(view)}
        onClick={(e) => {
          e.preventDefault();
          navigateTo(view);
        }}
        className="text-slate-500 hover:text-indigo-600 dark:hover:text-indigo-400 text-sm transition-colors text-left"
      >
        {children}
      </a>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 font-sans text-slate-900 dark:text-slate-100 transition-colors duration-200 flex flex-col">
      {/* Top Navigation */}
      <TopNav
        currentView={currentView}
        onNavigate={navigateTo}
        darkMode={darkMode}
        onToggleDarkMode={toggleDarkMode}
      />

      {/* Global Modal */}
      <Modal
        isOpen={modalConfig.isOpen}
        onClose={closeModal}
        onConfirm={modalConfig.onConfirm}
        title={modalConfig.title}
        message={modalConfig.message}
        type={modalConfig.type}
        confirmText={modalConfig.confirmText}
      />

      <FeedbackModal isOpen={feedbackOpen} onClose={() => setFeedbackOpen(false)} />

      <ValidationModal
        isOpen={validationOpen}
        onClose={() => setValidationOpen(false)}
        result={state.validationResult}
        isLoading={state.isValidating}
      />

      {/* Loading Overlay - Only for Analysis (Fetching) */}
      {isAnalyzing && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-800 rounded-xl p-8 shadow-2xl flex flex-col items-center max-w-sm text-center border border-slate-100 dark:border-slate-700">
            <Icons.Spinner className="animate-spin text-indigo-600 dark:text-indigo-400 mb-4 h-10 w-10" />
            <h3 className="text-lg font-bold text-slate-900 dark:text-white">Analyzing Module</h3>
            <p className="text-slate-500 dark:text-slate-400 mt-2 text-sm">
              {state.loadingMessage}
            </p>
          </div>
        </div>
      )}

      {/* Header Area */}
      <div className="relative pt-12 pb-8 sm:pb-12 px-4">
        {/* Top Right Actions - Hidden on mobile, use TopNav instead */}
        <div className="hidden md:flex absolute top-6 right-6 sm:right-12 items-center gap-3">
          <button
            onClick={() => setFeedbackOpen(true)}
            className="flex items-center text-slate-600 hover:text-indigo-600 dark:text-slate-400 dark:hover:text-indigo-400 font-medium text-sm px-3 py-2 rounded-md hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            title="Give Feedback"
          >
            <Icons.MessageSquare className="w-4 h-4 mr-1.5" />
            <span className="hidden lg:inline">Feedback</span>
          </button>

          <button
            onClick={() => navigateTo('guide')}
            className="flex items-center text-slate-600 hover:text-indigo-600 dark:text-slate-400 dark:hover:text-indigo-400 font-medium text-sm px-3 py-2 rounded-md hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          >
            <Icons.Help className="w-4 h-4 mr-1.5" />
            <span className="hidden lg:inline">Help</span>
          </button>

          <button
            onClick={handleResetRequest}
            className="flex items-center bg-white dark:bg-slate-800 border border-red-200 dark:border-red-900/50 hover:bg-red-50 dark:hover:bg-red-900/20 text-red-600 dark:text-red-400 text-sm font-medium px-3 py-2 rounded-md shadow-sm transition-colors"
          >
            <Icons.Refresh className="w-4 h-4 mr-1.5" />
            <span className="hidden lg:inline">Reset</span>
          </button>
        </div>

        <div className="max-w-4xl mx-auto text-center px-2">
          <div className="flex justify-center mb-4 sm:mb-6">
            <Logo />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 dark:text-white tracking-tight mb-2 sm:mb-3 px-4">
            TF Override Generator
          </h1>
          <p className="text-base sm:text-lg text-slate-500 dark:text-slate-400 px-4">
            Create Terraform override files with a modern, guided experience.
          </p>
          <div className="flex flex-wrap justify-center gap-3 mt-4 px-4">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 text-xs font-medium border border-emerald-200 dark:border-emerald-800">
              <Icons.ShieldCheck className="w-3.5 h-3.5" />
              100% Browser-Based
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 text-xs font-medium border border-blue-200 dark:border-blue-800">
              <Icons.Lock className="w-3.5 h-3.5" />
              No Data Sent Anywhere
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-400 text-xs font-medium border border-purple-200 dark:border-purple-800">
              <Icons.Zap className="w-3.5 h-3.5" />
              Instant Generation
            </span>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 pb-12 flex-grow w-full">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-6 lg:gap-8 items-start">
          {/* Left Column: Configuration Flow */}
          <div className="lg:col-span-5 space-y-6">
            {/* 1. Project Setup */}
            <StepCard title="1. Project Setup" icon={<Icons.Settings className="h-5 w-5" />}>
              <div className="space-y-5">
                <div>
                  <LabelWithTooltip
                    label="Tool"
                    tooltip="Select the Infrastructure as Code tool you are using."
                  />
                  <select
                    value={state.tool}
                    onChange={(e) =>
                      setState((prev) => ({ ...prev, tool: e.target.value as Tool }))
                    }
                    className={inputClass}
                  >
                    <option value={Tool.TERRAFORM}>Terraform</option>
                    <option value={Tool.TERRAGRUNT}>Terragrunt</option>
                    <option value={Tool.OPENTOFU}>OpenTofu</option>
                  </select>
                </div>

                <div>
                  <LabelWithTooltip
                    label="Cloud Provider"
                    tooltip="Choose the cloud platform where your resources will be deployed."
                  />
                  <select
                    value={state.provider}
                    onChange={(e) => {
                      const newProvider = e.target.value as CloudProvider;
                      // Reset region to empty to force selection
                      setState((prev) => ({
                        ...prev,
                        provider: newProvider,
                        region: '',
                      }));
                    }}
                    className={inputClass}
                  >
                    {Object.values(CloudProvider).map((p) => (
                      <option key={p} value={p}>
                        {p.toUpperCase()}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <LabelWithTooltip
                    label="Region"
                    tooltip="Select the geographical region for your cloud resources."
                  />
                  <select value={state.region} onChange={handleRegionChange} className={inputClass}>
                    <option value="">- Select a region -</option>
                    {REGIONS[state.provider] &&
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      REGIONS[state.provider].map((r: any) => (
                        <option key={r.id} value={r.id}>
                          {r.name} - {r.id}
                        </option>
                      ))}
                  </select>
                </div>

                {state.provider === CloudProvider.GCP && (
                  <div>
                    <LabelWithTooltip
                      label="GCP Project ID"
                      tooltip="Enter the Project ID for your Google Cloud project."
                    />
                    <input
                      type="text"
                      value={state.gcpProjectId}
                      onChange={(e) =>
                        setState((prev) => ({ ...prev, gcpProjectId: e.target.value }))
                      }
                      placeholder="my-project-id"
                      className={inputClass}
                    />
                  </div>
                )}
              </div>
            </StepCard>

            {/* 2. Module Source */}
            <StepCard title="2. Module Source" icon={<Icons.Github className="h-5 w-5" />}>
              <div className="space-y-4">
                {/* Popular Modules Dropdown */}
                {popularModules.length > 0 && (
                  <div className="relative">
                    <div className="flex justify-between items-center mb-1.5">
                      <LabelWithTooltip
                        label="Popular Modules"
                        tooltip="Quickly select a widely used module for your provider."
                      />
                      {isModuleLocked && (
                        <button
                          onClick={handleChangeModuleRequest}
                          className="text-xs text-indigo-600 hover:text-indigo-500 font-medium"
                        >
                          Change Module
                        </button>
                      )}
                    </div>
                    <select
                      value={selectedPopularModule}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val) {
                          setState((prev) => ({ ...prev, moduleUrl: val, error: null }));
                        }
                      }}
                      disabled={isModuleLocked}
                      className={inputClass}
                    >
                      <option value="">Custom / Manual Input</option>
                      {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                      {popularModules.map((mod: any) => (
                        <option key={mod.url} value={mod.url}>
                          {mod.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div>
                  <div className="flex justify-between items-center mb-1.5">
                    <LabelWithTooltip
                      label="Public GitHub URL"
                      tooltip="The HTTPS URL of the public GitHub repository containing the Terraform module."
                    />
                    {isModuleLocked && !popularModules.length && (
                      <button
                        onClick={handleChangeModuleRequest}
                        className="text-xs text-indigo-600 hover:text-indigo-500 font-medium"
                      >
                        Change Module
                      </button>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={state.moduleUrl}
                      onChange={handleUrlChange}
                      placeholder="e.g., https://github.com/terraform-aws-modules/terraform-aws-vpc"
                      className={inputClass}
                      disabled={isModuleLocked}
                    />
                    {!isModuleLocked && (
                      <button
                        onClick={analyzeModule}
                        disabled={!state.moduleUrl || isAnalyzing}
                        className="px-4 py-2.5 bg-slate-100 hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 rounded-md font-medium text-sm border border-slate-200 dark:border-slate-600 transition-colors disabled:opacity-50 min-h-[44px] whitespace-nowrap"
                      >
                        {isAnalyzing ? '...' : 'Analyze'}
                      </button>
                    )}
                    {isModuleLocked && (
                      <button
                        onClick={handleChangeModuleRequest}
                        className="px-4 py-2.5 bg-slate-50 dark:bg-slate-800 text-slate-400 border border-slate-200 dark:border-slate-700 rounded-md min-h-[44px]"
                      >
                        <Icons.Check className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                </div>

                {/* Error Message */}
                {state.error && (
                  <div className="rounded-md bg-red-50 dark:bg-red-900/20 p-3 flex items-start border border-red-100 dark:border-red-900/50">
                    <Icons.Warning className="h-5 w-5 text-red-500 dark:text-red-400 mt-0.5 mr-2 shrink-0" />
                    <p className="text-sm text-red-700 dark:text-red-300">{state.error}</p>
                  </div>
                )}
              </div>
            </StepCard>

            {/* 3. Variable Overrides */}
            <StepCard title="3. Variable Overrides" icon={<Icons.List className="h-5 w-5" />}>
              <div className="space-y-5">
                <div>
                  <label className={`${labelClass} mb-1.5`}>Variable Name</label>
                  <select
                    value={selectedVarName}
                    onChange={(e) => {
                      const name = e.target.value;
                      setSelectedVarName(name);
                      // Pre-fill value if editing an existing override
                      const existing = state.stagedOverrides.find((o) => o.name === name);
                      if (existing) {
                        setNewVarValue(existing.value);
                      } else {
                        // Pre-fill based on default or type if available
                        const variable = state.variables.find((v) => v.name === name);
                        if (variable) {
                          // If default exists (and isn't null/undefined), use it
                          if (variable.default !== undefined && variable.default !== null) {
                            // Use utility to convert object defaults to HCL format
                            if (typeof variable.default === 'object') {
                              setNewVarValue(convertJsValueToHcl(variable.default));
                            } else {
                              setNewVarValue(String(variable.default));
                            }
                          } else {
                            // Default is null or undefined
                            if (variable.type.includes('bool')) {
                              setNewVarValue('true');
                            } else {
                              setNewVarValue('');
                            }
                          }
                        } else {
                          setNewVarValue('');
                        }
                      }
                    }}
                    disabled={state.variables.length === 0}
                    className={`${inputClass} disabled:bg-slate-50 disabled:dark:bg-slate-800 disabled:text-slate-400 disabled:dark:text-slate-500`}
                  >
                    <option value="">- Fetch variables from module -</option>
                    {state.variables.map((v) => (
                      <option key={v.name} value={v.name} title={v.description || ''}>
                        {v.name} {v.required ? '*' : ''} ({v.type})
                      </option>
                    ))}
                  </select>
                  {selectedVarDef && selectedVarDef.description && (
                    <p className="mt-2 text-xs text-slate-500 dark:text-slate-400 flex items-start bg-slate-50 dark:bg-slate-900/50 p-2 rounded border border-slate-100 dark:border-slate-800">
                      <Icons.Info className="w-3.5 h-3.5 mr-2 mt-0.5 text-indigo-500 dark:text-indigo-400 shrink-0" />
                      {selectedVarDef.description}
                    </p>
                  )}
                </div>

                <div>
                  <label className={`${labelClass} mb-1.5`}>Variable Value</label>
                  <input
                    type="text"
                    value={newVarValue}
                    onChange={(e) => setNewVarValue(e.target.value)}
                    placeholder={getValuePlaceholder(selectedVarDef?.type)}
                    className={inputClass}
                  />
                </div>

                <div className="flex justify-end">
                  <button
                    onClick={addOverride}
                    disabled={!selectedVarName || !newVarValue}
                    className="flex items-center px-4 py-2.5 border border-slate-200 dark:border-slate-600 text-sm font-medium rounded-md shadow-sm text-slate-600 dark:text-slate-300 bg-white dark:bg-slate-700 hover:bg-slate-50 dark:hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors min-h-[44px]"
                  >
                    {isEditing ? (
                      <Icons.Check className="h-4 w-4 mr-2" />
                    ) : (
                      <Icons.Plus className="h-4 w-4 mr-2" />
                    )}
                    {isEditing ? 'Update Variable' : 'Add Variable'}
                  </button>
                </div>

                {/* Staged List */}
                {state.stagedOverrides.length > 0 && (
                  <div className="mt-6 pt-6 border-t border-slate-100 dark:border-slate-700">
                    <h4 className="text-xs font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-3">
                      Staged Overrides
                    </h4>
                    <div className="space-y-2">
                      {state.stagedOverrides.map((o) => (
                        <div
                          key={o.id}
                          className="group flex items-center justify-between bg-slate-50 dark:bg-slate-900 p-3 rounded-md border border-slate-100 dark:border-slate-700 hover:border-slate-200 dark:hover:border-slate-600 transition-colors"
                        >
                          <div className="flex flex-col min-w-0">
                            <span className="font-medium text-sm text-slate-800 dark:text-slate-200 truncate">
                              {o.name}
                            </span>
                            <span className="text-slate-500 dark:text-slate-400 text-xs font-mono truncate">
                              {o.value}
                            </span>
                          </div>
                          <button
                            onClick={() => removeOverride(o.id)}
                            className="text-slate-400 hover:text-red-500 dark:hover:text-red-400 p-1.5 rounded hover:bg-white dark:hover:bg-slate-800 transition-all opacity-0 group-hover:opacity-100"
                          >
                            <Icons.Trash className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </StepCard>

            {/* 4. Generate Files */}
            <StepCard title="4. Generate Files" icon={<Icons.Play className="h-5 w-5" />}>
              <div className="space-y-4">
                <p className="text-slate-600 dark:text-slate-300 text-sm">
                  Generate files instantly in your browser. No data is sent anywhere.
                </p>
                <button
                  onClick={generateFiles}
                  disabled={!canGenerate}
                  className="w-full flex items-center justify-center px-6 py-3.5 border border-transparent text-base font-medium rounded-lg shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-600 dark:hover:bg-indigo-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-slate-200 disabled:dark:bg-slate-700 disabled:text-slate-400 disabled:dark:text-slate-500 disabled:cursor-not-allowed transition-all"
                >
                  <Icons.Settings className="mr-2 h-5 w-5" />
                  Generate Files
                </button>
              </div>
            </StepCard>

            {/* AD PLACEMENT: Bottom of Configuration Column */}
            <AdUnit slotId="1234567890" />
          </div>

          {/* Right Column: Output */}
          <div id="output-section" className="lg:col-span-7 lg:sticky lg:top-8">
            <div className="bg-[#1E1E1E] rounded-xl shadow-2xl border border-slate-800 overflow-hidden min-h-[600px] flex flex-col">
              {state.isLoading ? (
                // Generating State Skeleton UI
                <div className="flex flex-col h-full animate-in fade-in duration-300">
                  <div className="flex items-center justify-between px-4 py-3 bg-[#252526] border-b border-[#333]">
                    <div className="flex space-x-1">
                      <div className="h-6 w-20 bg-[#333] rounded animate-pulse"></div>
                      <div className="h-6 w-16 bg-[#333] rounded animate-pulse"></div>
                      <div className="h-6 w-24 bg-[#333] rounded animate-pulse"></div>
                    </div>
                  </div>
                  <div className="flex-1 p-6 space-y-4">
                    <div className="flex items-center space-x-3 mb-6">
                      <Icons.Spinner className="text-indigo-500 animate-spin w-5 h-5" />
                      <span className="text-slate-400 text-sm font-mono">
                        Fetching and parsing module...
                      </span>
                    </div>
                    <div className="h-4 bg-[#333] rounded w-3/4 animate-pulse"></div>
                    <div className="h-4 bg-[#333] rounded w-1/2 animate-pulse"></div>
                    <div className="h-4 bg-[#333] rounded w-5/6 animate-pulse"></div>
                    <div className="h-4 bg-[#333] rounded w-2/3 animate-pulse"></div>
                  </div>
                </div>
              ) : state.generatedFiles ? (
                <>
                  <div className="flex items-center justify-between px-4 py-3 bg-[#252526] border-b border-[#333]">
                    <div className="flex space-x-1">
                      {(['provider', 'main', 'overrides'] as const).map((tab) => (
                        <button
                          key={tab}
                          onClick={() => setActiveTab(tab)}
                          className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                            activeTab === tab
                              ? 'bg-[#37373D] text-white'
                              : 'text-[#969696] hover:text-white hover:bg-[#2A2D2E]'
                          }`}
                        >
                          {getFileName(tab)}
                        </button>
                      ))}
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={handleValidate}
                        className="flex items-center px-3 py-1.5 rounded text-xs font-medium text-emerald-500 hover:text-emerald-400 hover:bg-[#2A2D2E] transition-colors"
                        title="Validate & Dry Run"
                      >
                        <Icons.ShieldCheck className="mr-1.5 h-3.5 w-3.5" />
                        Validate
                      </button>
                      <button
                        onClick={handleShareEmail}
                        className="flex items-center px-3 py-1.5 rounded text-xs font-medium text-[#969696] hover:text-white hover:bg-[#2A2D2E] transition-colors"
                        title="Share via Email"
                      >
                        <Icons.Share className="mr-1.5 h-3.5 w-3.5" />
                        Share
                      </button>
                      <button
                        onClick={handleHeaderCopy}
                        className="flex items-center px-3 py-1.5 rounded text-xs font-medium text-[#969696] hover:text-white hover:bg-[#2A2D2E] transition-colors"
                        title="Copy content"
                      >
                        {headerCopied ? (
                          <Icons.Check className="mr-1.5 h-3.5 w-3.5 text-green-500" />
                        ) : (
                          <Icons.Copy className="mr-1.5 h-3.5 w-3.5" />
                        )}
                        {headerCopied ? 'Copied' : 'Copy'}
                      </button>
                      <button
                        onClick={downloadZip}
                        className="flex items-center px-3 py-1.5 rounded text-xs font-medium text-white bg-indigo-600 hover:bg-indigo-700 transition-colors"
                      >
                        <Icons.Download className="mr-1.5 h-3.5 w-3.5" />
                        Download
                      </button>
                    </div>
                  </div>
                  <div className="flex-1 overflow-hidden relative bg-[#1E1E1E]">
                    <div className="absolute inset-0 overflow-auto custom-scrollbar">
                      {activeTab === 'provider' && (
                        <CodeBlock language="hcl" code={state.generatedFiles.provider} />
                      )}
                      {activeTab === 'main' && (
                        <CodeBlock language="hcl" code={state.generatedFiles.main} />
                      )}
                      {activeTab === 'overrides' && (
                        <CodeBlock language="hcl" code={state.generatedFiles.overrides} />
                      )}
                    </div>
                  </div>
                </>
              ) : (
                <div className="flex-1 flex flex-col items-center justify-center text-slate-500 p-12 text-center">
                  <div className="w-20 h-20 bg-white/5 rounded-full flex items-center justify-center mb-6">
                    <Icons.Code className="h-10 w-10 text-slate-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-300 mb-2">Ready to Generate</h3>
                  <p className="text-slate-400 max-w-sm mx-auto text-sm leading-relaxed">
                    Complete the setup steps on the left to generate your production-ready Terraform
                    override configurations.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* AD PLACEMENT: Footer Banner */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full flex flex-col items-center">
        <AdUnit slotId="0987654321" format="horizontal" className="mb-8 w-full" />
      </div>

      {/* SEO Content: Below the Fold */}
      <SeoContent />

      {/* Footer Links */}
      <footer className="w-full border-t border-slate-200 dark:border-slate-800 mt-12 py-10 bg-white dark:bg-slate-900">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            <div>
              <h3 className="text-sm font-semibold text-slate-900 dark:text-white uppercase tracking-wider mb-4">
                Legal
              </h3>
              <ul className="space-y-3">
                <li>
                  <NavLink view="privacy">Privacy Policy</NavLink>
                </li>
                <li>
                  <NavLink view="terms">Terms of Service</NavLink>
                </li>
                <li>
                  <a
                    href="/sitemap.xml"
                    target="_self"
                    className="text-slate-500 hover:text-indigo-600 dark:hover:text-indigo-400 text-sm transition-colors text-left"
                  >
                    Sitemap
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="text-sm font-semibold text-slate-900 dark:text-white uppercase tracking-wider mb-4">
                Team
              </h3>
              <ul className="space-y-3">
                <li>
                  <NavLink view="about">About Us</NavLink>
                </li>
                <li>
                  <NavLink view="contact">Contact Us</NavLink>
                </li>
                <li>
                  <button
                    onClick={() => setFeedbackOpen(true)}
                    className="text-slate-500 hover:text-indigo-600 dark:hover:text-indigo-400 text-sm transition-colors text-left"
                  >
                    Feedback
                  </button>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="text-sm font-semibold text-slate-900 dark:text-white uppercase tracking-wider mb-4">
                Documentation
              </h3>
              <ul className="space-y-3">
                <li>
                  <NavLink view="guide">How to Use</NavLink>
                </li>
                <li>
                  <NavLink view="guide-overrides">Terraform Overrides Guide</NavLink>
                </li>
                <li>
                  <NavLink view="guide-examples">Override Examples (AWS/Azure/GCP)</NavLink>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="text-sm font-semibold text-slate-900 dark:text-white uppercase tracking-wider mb-4">
                Advanced Topics
              </h3>
              <ul className="space-y-3">
                <li>
                  <NavLink view="guide-precedence">Variable Precedence</NavLink>
                </li>
                <li>
                  <NavLink view="guide-terragrunt">Terragrunt Overrides</NavLink>
                </li>
                <li>
                  <NavLink view="guide-best-practices">Override Best Practices</NavLink>
                </li>
                <li>
                  <NavLink view="guide-troubleshooting">Troubleshooting Guide</NavLink>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="text-sm font-semibold text-slate-900 dark:text-white uppercase tracking-wider mb-4">
                Related Tools
              </h3>
              <ul className="space-y-3">
                <li>
                  <a
                    href="https://localdiff.dev/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-slate-500 hover:text-indigo-600 dark:hover:text-indigo-400 text-sm transition-colors inline-flex items-center group"
                  >
                    <Icons.ExternalLink className="w-3 h-3 mr-1.5" />
                    LocalDiff.dev
                  </a>
                </li>
              </ul>
              <p className="text-xs text-slate-400 dark:text-slate-500 mt-3">
                Tools for DevOps Engineers
              </p>
            </div>
          </div>

          <div className="border-t border-slate-200 dark:border-slate-800 pt-8 flex flex-col md:flex-row justify-between items-center text-sm text-slate-500 dark:text-slate-400">
            <div className="flex items-center space-x-2">
              <span>
                &copy; {new Date().getFullYear()} TF Override Generator. All rights reserved.
              </span>
              <span className="text-xs font-mono bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded border border-slate-200 dark:border-slate-700">
                v2.0.0
              </span>
            </div>
            <div className="mt-4 md:mt-0">
              <a
                href="https://buymeacoffee.com/senet"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-4 py-2 bg-[#FFDD00] hover:bg-[#FFEA00] text-black text-sm font-semibold rounded-full shadow-md transition-transform hover:scale-105 active:scale-95"
              >
                <Icons.Coffee className="w-4 h-4 mr-2" />
                Buy me a coffee
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default App;
